import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Sparkles, ArrowRight, Copy, Gift } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

function ReferralShareCard({ code, firstName }) {
  const [copied, setCopied] = useState(false);
  const shareText = `${firstName ? firstName + " here — " : ""}I just booked with Melissa O'Donnell (she's incredible). Use my code ${code} for $25 off your booking deposit: ${window.location.origin}/#contact`;

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success("Code copied");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Couldn't copy — long-press to select");
    }
  };

  const copyShareMessage = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      toast.success("Share message copied — paste it anywhere");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  return (
    <div
      data-testid="referral-share-card"
      className="mt-10 bg-melissa-bg border border-melissa-border p-8 md:p-10"
    >
      <div className="flex items-center gap-2 mb-4">
        <Gift size={16} className="text-melissa-rose" />
        <p className="text-xs uppercase tracking-overline text-melissa-rose">A thank-you for sharing</p>
      </div>
      <h2 className="font-serif text-3xl md:text-4xl text-melissa-ink leading-tight">
        Your friends get <em className="italic text-melissa-rose">$25 off.</em>{" "}
        <span className="block sm:inline">You get $25 back.</span>
      </h2>
      <p className="mt-3 text-melissa-inkSoft font-light leading-relaxed max-w-xl">
        Share your personal code with anyone planning a wedding, photoshoot, or special occasion.
        Each booking that uses it sends $25 of credit your way (Melissa applies it to your next service).
      </p>

      <div className="mt-8 flex flex-col sm:flex-row gap-4">
        <div className="flex-1 flex items-center justify-between bg-melissa-bg2 border border-melissa-border px-5 py-4">
          <div>
            <div className="text-[10px] uppercase tracking-overline text-melissa-inkSoft">Your code</div>
            <div
              data-testid="referral-code-display"
              className="font-mono font-semibold tracking-widest text-melissa-ink text-xl mt-1"
            >
              {code}
            </div>
          </div>
          <button
            onClick={copyCode}
            data-testid="referral-copy-button"
            className="inline-flex items-center gap-2 px-4 py-3 border border-melissa-ink text-melissa-ink text-[10px] uppercase tracking-overline hover:bg-melissa-ink hover:text-melissa-bg transition-colors"
          >
            <Copy size={12} />
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
        <button
          onClick={copyShareMessage}
          data-testid="referral-share-button"
          className="inline-flex items-center justify-center gap-2 px-6 py-4 bg-melissa-ink text-melissa-bg text-[10px] uppercase tracking-overline hover:bg-melissa-rose transition-colors"
        >
          Copy Share Message
        </button>
      </div>
    </div>
  );
}

export default function BookingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | expired | timeout | error
  const [details, setDetails] = useState(null);
  const [upsell, setUpsell] = useState(null);
  const [upsellLoading, setUpsellLoading] = useState(false);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;
    const MAX_ATTEMPTS = 10;
    const INTERVAL = 2000;

    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setDetails(data);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (data.status === "expired") {
          setState("expired");
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState("timeout");
          return;
        }
        setTimeout(poll, INTERVAL);
      } catch (e) {
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState("error");
        } else {
          setTimeout(poll, INTERVAL);
        }
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  // Fetch upsell packages once deposit confirmed
  useEffect(() => {
    if (state === "paid" && details?.purpose === "deposit") {
      api.get("/payments/upsell-packages").then((r) => {
        const pkg = r.data.packages?.[0];
        if (pkg) setUpsell(pkg);
      }).catch(() => {});
    }
  }, [state, details]);

  const acceptUpsell = async () => {
    if (!details?.booking_id || !upsell) return;
    setUpsellLoading(true);
    try {
      const { data } = await api.post("/payments/upsell-checkout", {
        booking_id: details.booking_id,
        package_id: upsell.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't open checkout";
      toast.error(typeof msg === "string" ? msg : "Something went wrong");
      setUpsellLoading(false);
    }
  };

  const isUpsellPaid = state === "paid" && details?.purpose === "upsell";

  return (
    <div data-testid="booking-success-page" className="min-h-screen bg-melissa-bg flex items-center justify-center p-6">
      <div className="w-full max-w-2xl bg-melissa-bg2 border border-melissa-border p-10 md:p-14">
        {state === "polling" && (
          <div data-testid="booking-success-polling" className="text-center">
            <Loader2 className="mx-auto mb-6 animate-spin text-melissa-rose" size={32} />
            <p className="text-xs uppercase tracking-overline text-melissa-rose mb-3">Confirming…</p>
            <h1 className="font-serif font-light text-4xl md:text-5xl text-melissa-ink leading-tight">
              Checking your payment.
            </h1>
            <p className="mt-4 text-sm text-melissa-inkSoft">This usually takes a few seconds.</p>
          </div>
        )}

        {state === "paid" && (
          <div data-testid="booking-success-paid">
            <div className="text-center">
              <CheckCircle2 className="mx-auto mb-6 text-emerald-600" size={40} />
              <p className="text-xs uppercase tracking-overline text-melissa-rose mb-3">
                {isUpsellPaid ? "Bundle added" : "Your date is reserved"}
              </p>
              <h1 className="font-serif font-light text-4xl md:text-5xl text-melissa-ink leading-tight">
                {isUpsellPaid ? "Beautifully done." : "Thank you — see you soon."}
              </h1>
              <p className="mt-4 text-melissa-inkSoft font-light leading-relaxed">
                Your <span className="text-melissa-ink">${details?.amount_total?.toFixed(2)}</span>{" "}
                {isUpsellPaid ? "bundle is confirmed" : "deposit is received"}.
                Melissa will be in touch within 48 hours to confirm the final details.
              </p>
            </div>

            {/* Upsell offer */}
            {!isUpsellPaid && upsell && (
              <div
                data-testid="upsell-offer"
                className="mt-10 bg-melissa-bg border border-melissa-rose/30 p-8 md:p-10 relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 px-3 py-1 bg-melissa-rose text-melissa-bg text-[10px] uppercase tracking-overline">
                  Save ${upsell.savings?.toFixed(0)}
                </div>
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles size={16} className="text-melissa-rose" />
                  <p className="text-xs uppercase tracking-overline text-melissa-rose">A small offer, only here</p>
                </div>
                <h2 className="font-serif text-3xl md:text-4xl text-melissa-ink leading-tight">
                  {upsell.name}
                </h2>
                <p className="mt-3 text-melissa-inkSoft font-light leading-relaxed">
                  {upsell.description} Add the pre-wedding trial run to your booking now and
                  Melissa will pair both sessions on a single, calmer timeline.
                </p>
                <div className="mt-6 flex items-baseline gap-3">
                  <span className="font-serif text-3xl text-melissa-ink">${upsell.amount?.toFixed(0)}</span>
                  <span className="text-melissa-inkSoft text-sm line-through">${upsell.original?.toFixed(0)}</span>
                  <span className="text-xs uppercase tracking-overline text-melissa-rose">
                    saves ${upsell.savings?.toFixed(0)}
                  </span>
                </div>
                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <button
                    onClick={acceptUpsell}
                    disabled={upsellLoading}
                    data-testid="upsell-accept-button"
                    className="group inline-flex items-center gap-3 px-7 py-4 bg-melissa-ink text-melissa-bg text-xs uppercase tracking-overline hover:bg-melissa-rose transition-colors duration-300 disabled:opacity-60"
                  >
                    {upsellLoading ? "Opening checkout…" : `Add Bundle — $${upsell.amount?.toFixed(0)}`}
                    <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                  </button>
                  <span className="text-[11px] text-melissa-inkSoft">
                    No pressure — Melissa will still offer the trial after.
                  </span>
                </div>
              </div>
            )}

            {/* Referral share card */}
            {details?.referral_code && (
              <ReferralShareCard
                code={details.referral_code}
                firstName={details.client_first_name}
              />
            )}

            <div className="mt-10 text-center">
              <Link
                to="/"
                data-testid="booking-success-home-link"
                className="inline-flex items-center px-7 py-4 border border-melissa-ink text-melissa-ink text-xs uppercase tracking-overline hover:bg-melissa-ink hover:text-melissa-bg transition-colors duration-300"
              >
                {isUpsellPaid ? "Return Home" : (upsell ? "Skip & Return Home" : "Return Home")}
              </Link>
            </div>
          </div>
        )}

        {(state === "expired" || state === "timeout" || state === "error") && (
          <div data-testid="booking-success-error" className="text-center">
            <XCircle className="mx-auto mb-6 text-red-500" size={40} />
            <p className="text-xs uppercase tracking-overline text-red-600 mb-3">Something went off</p>
            <h1 className="font-serif font-light text-3xl md:text-4xl text-melissa-ink leading-tight">
              We couldn't confirm your payment.
            </h1>
            <p className="mt-4 text-melissa-inkSoft">
              {state === "expired"
                ? "The checkout session expired before payment completed."
                : "We're still waiting on confirmation from the payment provider. If you were charged, please reach out — we'll sort it personally."}
            </p>
            <div className="mt-8 flex flex-wrap gap-4 justify-center">
              <Link
                to="/#contact"
                data-testid="booking-retry-link"
                className="px-7 py-4 bg-melissa-ink text-melissa-bg text-xs uppercase tracking-overline hover:bg-melissa-rose"
              >
                Try Again
              </Link>
              <a
                href="mailto:melissaruthodonnell@gmail.com"
                data-testid="booking-email-link"
                className="px-7 py-4 border border-melissa-ink text-xs uppercase tracking-overline hover:bg-melissa-ink hover:text-melissa-bg"
              >
                Email Melissa
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
